import { t } from 'i18next';
import { FC, useState } from 'react';

import { analyticsDatabaseName, clearDatabase } from '@treams/treams-analytics';
import { sessionSlotSwitchInFlight, useWorkspaceAlias } from '@treams/treams-hooks';
import {
	User,
	useImpersonateSessionSlotMutation,
	useObfuscateUserMutation,
} from '@treams/treams-network';
import {
	CircleUserIcon,
	ContextMenu,
	IconButton,
	JunkIcon,
	MoreAlternateIcon,
	Popover,
} from '@treams/treams-ui';

type Props = {
	user: User;
	refetch: () => void;
};

const AuthenticateAsUser: FC<Omit<Props, 'refetch'>> = ({ user }) => {
	const workspaceAlias = useWorkspaceAlias();
	const isLocalHost = window.location.hostname === 'localhost';
	const [impersonateSessionSlot] = useImpersonateSessionSlotMutation();

	const authenticateAsUser = async () => {
		// The mutation changes the active slot server-side; suppress the
		// reload-on-mismatch handling in useHttpLink so concurrent requests
		// can't trigger a competing hard reload that races the cleanup below.
		sessionSlotSwitchInFlight.current = true;

		try {
			// Writes an impersonation slot into the server-side browser session;
			// after the reload every request runs as the target user.
			const response = await impersonateSessionSlot({
				variables: {
					input: {
						userID: user.id,
					},
				},
			});

			if (!response.data?.impersonateSessionSlot.success) {
				sessionSlotSwitchInFlight.current = false;
				return;
			}
		} catch (error) {
			sessionSlotSwitchInFlight.current = false;
			throw error;
		}

		// Impersonation reuses the target's own database, so clear it: a support
		// engineer should start from a fresh ingest rather than whatever this
		// machine cached for that user earlier.
		try {
			await clearDatabase(analyticsDatabaseName(workspaceAlias, user.id));
		} catch {
			// Continue with the reload even if cleanup fails
		}

		// The flag stays set through this deliberate reload.
		window.location.reload();
	};

	if (!isLocalHost) {
		return null;
	}

	return (
		<ContextMenu.Item
			disabled={user.isArchived}
			title={t('settings.employees.buttons.authenticateAsUser', {
				user: user.firstName,
			})}
			icon={<CircleUserIcon />}
			onClick={authenticateAsUser}
		/>
	);
};

const SupportUserMenu: FC<Props> = (props) => {
	const [obfuscate] = useObfuscateUserMutation();
	const [confirmAction, setConfirmAction] = useState('');
	const isObfuscated = props.user.username.startsWith('obfuscated+');

	const obfuscateUser = async () => {
		await obfuscate({
			variables: {
				input: { id: props.user.id, username: props.user.username },
			},
		});

		props.refetch();
	};

	return (
		<Popover.Confirm
			visible={!!confirmAction}
			title={t('common.confirm')}
			confirmTitle={t('settings.employees.buttons.obfuscate')}
			cancelTitle={t('common.cancel')}
			onCancel={() => setConfirmAction('')}
			onConfirm={() => {
				obfuscateUser();
				setConfirmAction('');
			}}
		>
			<ContextMenu.Target
				menu={
					<ContextMenu>
						<AuthenticateAsUser {...props} />

						<ContextMenu.Item
							disabled={isObfuscated}
							title={t('settings.employees.buttons.obfuscate')}
							type="destructive"
							icon={<JunkIcon />}
							onClick={() => {
								setConfirmAction('obfuscate');
							}}
						/>
					</ContextMenu>
				}
			>
				<IconButton type="secondary" icon={<MoreAlternateIcon />} />
			</ContextMenu.Target>
		</Popover.Confirm>
	);
};

export default SupportUserMenu;
